import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, ArrowLeft, TrendingUp, X, Zap, Calendar } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { searchArticles, getArticles } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';

const BORMULA_LOGO = "https://customer-assets.emergentagent.com/job_a6e857fc-9f8a-42a4-a234-830c8a3cc714/artifacts/98ozj9ol_Bormula%20Live%20Logo%201080x1350%20%281%29.png";

const CATEGORIES = [
  { value: '', label: 'All' },
  { value: 'news', label: 'News' },
  { value: 'race-updates', label: 'Race Updates' },
  { value: 'memes', label: 'Memes' },
  { value: 'reels', label: 'Reels' },
];

const categoryBadgeColors = {
  news: 'bg-[#CCFF00] text-black',
  memes: 'bg-purple-500 text-white',
  'race-updates': 'bg-red-500 text-white',
  reels: 'bg-blue-500 text-white',
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  });
};

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialCategory = searchParams.get('category') || '';

  const [query, setQuery] = useState(initialQuery);
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(!!initialQuery);

  useSEO({
    title: initialQuery ? 'Search: ' + initialQuery : 'All Articles',
    description: 'Browse all F1 articles on BORMULA - news, race updates, memes, and more.',
    url: 'https://bormula.com/search',
  });

  // Fetch all articles (with optional category filter)
  const fetchArticles = useCallback(async (category) => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (category) params.category = category;
      const data = await getArticles(params);
      setArticles(data);
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search articles
  const handleSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setIsSearching(false);
      fetchArticles(activeCategory);
      return;
    }
    setLoading(true);
    setIsSearching(true);
    try {
      const data = await searchArticles(searchQuery);
      setArticles(data);
    } catch (error) {
      console.error('Search error:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, fetchArticles]);

  // Initial load
  useEffect(() => {
    if (initialQuery) {
      handleSearch(initialQuery);
    } else {
      fetchArticles(initialCategory);
    }
  }, [initialQuery, initialCategory, handleSearch, fetchArticles]);

  const onSubmit = (e) => {
    e.preventDefault();
    const params = {};
    if (query.trim()) params.q = query;
    if (activeCategory) params.category = activeCategory;
    setSearchParams(params);
    if (query.trim()) {
      handleSearch(query);
    } else {
      fetchArticles(activeCategory);
    }
  };

  const onCategoryChange = (cat) => {
    setActiveCategory(cat);
    setQuery('');
    setIsSearching(false);
    const params = {};
    if (cat) params.category = cat;
    setSearchParams(params);
    fetchArticles(cat);
  };

  const clearSearch = () => {
    setQuery('');
    setIsSearching(false);
    const params = {};
    if (activeCategory) params.category = activeCategory;
    setSearchParams(params);
    fetchArticles(activeCategory);
  };

  return (
    <div className="min-h-screen bg-black" data-testid="search-page">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <img src={BORMULA_LOGO} alt="Bormula" className="h-10 w-10 object-contain" />
              <span className="font-display text-2xl font-bold text-white tracking-tighter">BORMULA</span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-white/80 hover:text-[#CCFF00] transition-colors"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </Link>
          </div>
        </div>
      </nav>

      {/* Header with Search + Category Filters */}
      <div className="pt-28 pb-8 px-4 sm:px-6 lg:px-8 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-[#CCFF00] mb-3 font-black uppercase tracking-widest text-sm">
            <Zap size={16} fill="#CCFF00" /> Browse
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tighter text-white mb-8" data-testid="page-heading">
            {isSearching ? 'Search Results' : 'All Articles'}
          </h1>

          {/* Search Bar */}
          <form onSubmit={onSubmit} className="relative mb-6">
            <Input
              type="text"
              placeholder="Search articles..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full h-12 pl-5 pr-24 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#CCFF00] rounded-xl"
              data-testid="search-input"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {query && (
                <button type="button" onClick={clearSearch} className="p-2 text-white/40 hover:text-white" data-testid="clear-search">
                  <X className="w-4 h-4" />
                </button>
              )}
              <Button type="submit" size="sm" className="bg-[#CCFF00] text-black hover:bg-[#b3e600] rounded-lg" data-testid="search-submit">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </form>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => onCategoryChange(cat.value)}
                className={`px-5 py-2 text-xs font-black uppercase tracking-widest rounded-full transition-all ${
                  activeCategory === cat.value
                    ? 'bg-[#CCFF00] text-black'
                    : 'bg-white/5 text-white/50 border border-white/10 hover:text-white hover:border-white/30'
                }`}
                data-testid={`filter-${cat.value || 'all'}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {isSearching && (
            <p className="text-xs font-bold uppercase tracking-widest text-white/30 mb-6" data-testid="results-count">
              {articles.length} {articles.length === 1 ? 'result' : 'results'} for "{searchParams.get('q')}"
            </p>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="spinner" />
            </div>
          ) : articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="articles-grid">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group"
                  data-testid={`article-card-${article.slug}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden rounded-2xl mb-4">
                    <img
                      src={article.image_url || 'https://images.unsplash.com/photo-1749952648908-d20b76c078dc'}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest ${categoryBadgeColors[article.category] || categoryBadgeColors.news}`}>
                        {article.category}
                      </span>
                    </div>
                    {article.trending && (
                      <div className="absolute top-4 right-4 flex items-center gap-1 bg-black/70 backdrop-blur-sm text-[#CCFF00] px-2 py-1 text-[10px] font-black uppercase tracking-wider rounded-sm">
                        <TrendingUp className="w-3 h-3" /> Trending
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
                  </div>

                  <div className="flex items-center gap-3 text-white/40 text-[10px] font-black uppercase tracking-widest mb-2">
                    {article.author_name && (
                      <>
                        <span>{article.author_name}</span>
                        <span className="w-1 h-1 bg-white/20 rounded-full" />
                      </>
                    )}
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(article.created_at)}</span>
                  </div>

                  <h3 className="font-display text-xl font-bold text-white uppercase leading-tight group-hover:text-[#CCFF00] transition-colors mb-2">
                    {article.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <p className="font-display text-2xl text-white mb-3">
                {isSearching ? 'No results found' : 'No articles yet'}
              </p>
              <p className="text-white/40">
                {isSearching ? 'Try different keywords or browse by category' : 'Check back soon for new content'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center gap-2">
            <img src={BORMULA_LOGO} alt="Bormula" className="h-8 w-8 object-contain" />
            <span className="font-display text-2xl font-bold text-[#CCFF00]">BORMULA</span>
          </Link>
          <p className="text-xs text-white/20 mt-2 font-bold uppercase tracking-widest">&copy; 2024 Bormula Media Group</p>
        </div>
      </footer>
    </div>
  );
}
